import { useState, type ReactNode } from "react";
import { HomeownerDeal } from "./useHomeownerAuth";

const LOGO_URL = "/img/bardin-logo.png";

/** Shared wrapper for all homeowner pages — handles loading, error, and manual auth */
export default function HomeownerGate({
  deal,
  loading,
  error,
  needsAuth,
  authenticate,
  title,
  children,
}: {
  deal: HomeownerDeal | null;
  loading: boolean;
  error: string | null;
  needsAuth: boolean;
  authenticate: (dealId: string, lastName: string, streetNum: string) => void;
  title: string;
  children: ReactNode;
}) {
  const [lastName, setLastName] = useState("");
  const [streetNum, setStreetNum] = useState("");

  const dealIdFromPath = window.location.pathname.split("/").pop() || "";

  if (loading) {
    return (
      <div className="min-h-screen bg-bardin-sky flex items-center justify-center">
        <div className="text-bardin-teal text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bardin-sky flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <img src={LOGO_URL} alt="Bardin Roofing" className="h-12 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            If you continue having trouble, text us at{" "}
            <a href="tel:+14708238816" className="text-bardin-teal font-medium">
              (470) 823-8816
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (needsAuth && !deal) {
    return (
      <div className="min-h-screen bg-bardin-sky flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <img src={LOGO_URL} alt="Bardin Roofing" className="h-12 mx-auto mb-6" />
          <h1 className="text-xl font-bold text-bardin-ink text-center mb-2">{title}</h1>
          <p className="text-sm text-gray-500 text-center mb-6">
            Please verify your identity to continue.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              authenticate(dealIdFromPath, lastName, streetNum);
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-bardin-ink mb-1">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                placeholder="Enter your last name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-bardin-ink mb-1">
                Street Number
              </label>
              <input
                type="text"
                value={streetNum}
                onChange={(e) => setStreetNum(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                placeholder="e.g. 1234"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition"
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!deal) return null;

  return (
    <div className="min-h-screen bg-bardin-sky">
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <img src={LOGO_URL} alt="Bardin Roofing" className="h-12" />
        <span className="text-sm text-gray-500">(470) 823-8816</span>
      </header>
      <main className="max-w-lg mx-auto p-4">{children}</main>
    </div>
  );
}
